import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import EventsAPI from 'dashboard/api/planning/events';
import {
  formatAuditActor,
  formatAuditChangeEntries,
} from 'dashboard/helper/planningEventAuditHistoryHelper';

const HISTORY_FETCH_TIMEOUT_MS = 10000;

export function useEventHistory(eventId) {
  const { t } = useI18n();

  const historyLoading = ref(false);
  const historyError = ref('');
  const historyRecords = ref([]);
  const isHistoryDialogOpen = ref(false);
  const historyMeta = ref({
    currentPage: 1,
    perPage: 20,
    totalEntries: 0,
  });
  let historyRequestToken = 0;

  const fetchHistory = async (page = 1) => {
    const eid =
      typeof eventId === 'function' ? eventId() : eventId.value || eventId;
    if (!eid || eid === 'undefined') return;
    const requestToken = historyRequestToken + 1;
    historyRequestToken = requestToken;
    historyLoading.value = true;
    historyError.value = '';
    try {
      const response = await Promise.race([
        EventsAPI.history(eid, { page }),
        new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error('history_fetch_timeout')),
            HISTORY_FETCH_TIMEOUT_MS
          );
        }),
      ]);
      if (requestToken !== historyRequestToken) return;
      const rawLogs = response.data.audit_logs || response.data.auditLogs || [];
      historyRecords.value = Array.isArray(rawLogs)
        ? rawLogs
        : Object.values(rawLogs);
      historyMeta.value = {
        currentPage: response.data.current_page || 1,
        perPage: response.data.per_page || 20,
        totalEntries: response.data.total_entries || 0,
      };
    } catch {
      if (requestToken !== historyRequestToken) return;
      historyError.value = t('PLANNING.SHOW.HISTORY_ERROR');
      historyRecords.value = [];
    } finally {
      if (requestToken !== historyRequestToken) return;
      historyLoading.value = false;
    }
  };

  const openHistoryDialog = async () => {
    // Close any focused menu/tooltip first so aria-hidden + click-outside
    // don't immediately dismiss the dialog opened from the header menu.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    isHistoryDialogOpen.value = true;
    await nextTick();
    fetchHistory(1);
  };

  const closeHistoryDialog = () => {
    isHistoryDialogOpen.value = false;
  };

  const onHistoryPageChange = async page => {
    await fetchHistory(page);
  };

  const getAuditChanges = audit =>
    formatAuditChangeEntries(audit, { includeActor: true });

  const actionLabel = action => {
    if (action === 'create') return t('PLANNING.SHOW.HISTORY_ACTION_CREATE');
    if (action === 'destroy') return t('PLANNING.SHOW.HISTORY_ACTION_DELETE');
    return t('PLANNING.SHOW.HISTORY_ACTION_UPDATE');
  };

  const actionBadgeClass = action => {
    if (action === 'create') return 'bg-green-100 text-green-700';
    if (action === 'destroy') return 'bg-red-100 text-red-700';
    return 'bg-purple-100 text-purple-700';
  };

  const hasHistory = computed(() => historyMeta.value.totalEntries > 0);

  return {
    historyLoading,
    historyError,
    historyRecords,
    historyMeta,
    isHistoryDialogOpen,
    hasHistory,
    fetchHistory,
    openHistoryDialog,
    closeHistoryDialog,
    onHistoryPageChange,
    getAuditChanges,
    actionLabel,
    actionBadgeClass,
    formatAuditActor,
  };
}
